<template>
  <h2>{{label}}</h2>
  <div v-if="errorMessage" ref="errorMessageRef" class="alert alert-danger my-2">
    <span><i :class="[ICONS.ErrorCircle, 'color-danger', '']"></i> {{ errorMessage }}</span>
  </div>
  <FileUpload
    ref="fileUploadRef"
    :name="name"
    :auto="true"
    :show-upload-button="false"
    :show-cancel-button="false"
    :multiple="fileLimit > 1"
    :file-limit="fileLimit"
    :accept="accept"
    :entity="entity"
    :network="network"
    :url="url"
    custom-upload
    @uploader="onUpload"
    :pt:input:aria-label="label"
    :choose-button-props="{'aria-label': 'Choose ' + (label ?? name)}"
    :upload-button-props="{'aria-label': 'Upload ' + (label ?? name)}"
    :cancel-button-props="{'aria-label': 'Cancel ' + (label ?? name)}"
    @removeUploadedFile="clear"
    @cancel="clear"
    @reset="clear"
  >
    <template #empty>
      <p class="dropzone">Drag and drop files here or select choose above.<br/>The selected file will be automatically renamed and uploaded.</p>
    </template>
  </FileUpload>
</template>

<script>
import {getPreSignedUrl} from '@/services/uploadFile.js';
import FileUpload from 'primevue/fileupload';
import axios from "axios";
import {useAppStore} from "@/stores/appStore.js";
import {ICONS} from "@/utils/constants.js";
import {DocumentModel} from "@/models/DocumentModel.js";
import {getDocuments, saveDocument, saveEditDocument} from "@/services/documentDBservice.js";
import {useAuthStore} from "@/stores/authStore.js";

export default {
  computed: {
    ICONS() {
      return ICONS;
    },
  },
  components: {
    FileUpload
  },
  props: {
    name: {
      type: String,
      required: true,
    },
    url: {
      type: String,
      default: '/undefined',
    },
    accept: {
      type: String,
      default: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    },
    entity: {
      type: Number,
      required: false,
    },
    network: {
      type: Number,
      required: false,
    },
    label: {
      type: String,
      required: false,
    },
    moduleType: {
      type: String,
      required: false,
    },
    ref: {
      type: String,
      required: false,
    },
    index: {
      type: Number,
      required: false,
    },
    fileLimit: {
      type: Number,
      required: false,
      default: 1,
    }
  },
  emits: ['update:url'],
  data() {
    return {
      url: '/initial-url',  // This is the URL that will be passed down
      errorMessage: '',
      ref: '',
    };
  },
  setup() {
    const appStore = useAppStore();
    const authStore = useAuthStore();

    return {
      appStore,authStore
    };
  },
  methods: {
    async onUpload(event) {
      let file = event.files[0];
      try {
        const renamedFile = await this.renameFile(file);
        file = renamedFile;
        this.files = [renamedFile];

        this.url = (await getPreSignedUrl(renamedFile)).url;
        this.$emit('update:url', this.url);
        this.errorMessage = '';
      } catch (error) {
        this.clear();
        this.handleError('Failed to get upload URL. Please try again.');
        return;
      }
      try {
        this.$refs.fileUploadRef.progress = 0;
        await axios.put(this.url, file, {
          headers: {
            "Content-Type": file.type
          },
          onUploadProgress: (event) => {
            if (event.lengthComputable) {
              this.$refs.fileUploadRef.progress = Math.round((event.loaded / event.total) * 100);
            }
          }
        });
        this.$refs.fileUploadRef.progress = 100;
        this.$refs.fileUploadRef.files = [];
        this.$refs.fileUploadRef.uploadedFiles.push(file);
        this.errorMessage = '';
        console.log("Upload successful!");
        this.saveDocumentData(file);
      } catch (error) {
        this.handleError('Upload failed. ' + error.message);
      }
    },

    async renameFile(file) {
      const fileType = file.type;
      const nameParts = file.name.split('.');
      const fileExtension = nameParts.pop();
      const originalName = nameParts.join('.');

      let newFileName = this.name;
      if (this.entity != null) {
        const entityName = this.appStore.getEntityNameById(this.entity);
        newFileName += '_' + this.getShortName(entityName);
      }
      if (this.network != null) {
        const networkId = this.appStore.getNetworkIdById(this.network);
        newFileName += '_' + this.getShortName(networkId);
      }
      newFileName += '_' + Date.now();
      if (originalName != null) {
        newFileName += '_' + this.getShortName(originalName);
      }
      newFileName += '.' + fileExtension;
      console.log("New filename: " + newFileName);

      return new File([file], newFileName, { type: fileType });
    },

    async getUploadedFiles() {
      const fileUpload = this.$refs.fileUploadRef;
      if (fileUpload && fileUpload.uploadedFiles) {
        return fileUpload.uploadedFiles;
      }
      return [];
    },

    getShortName(name) {
      return name
          .slice(0, 9)
          .replace(/[^a-zA-Z0-9.-]+/g, '-')
          .replace(/^_+|_+$/g, '');
    },

    async saveDocumentData(fileData){
      let docToSave = new DocumentModel();
      if(this.moduleType?.includes('Document Management Table')) {
        let docList = await getDocuments();
        let filteredDocs = docList.filter(doc => doc.id === this.index);
        let docToSave = filteredDocs.reduce((prev, current) => {
          return (prev.version > current.version) ? prev : current;
        }, filteredDocs[0]);
        docToSave.version = docToSave.version + 1;
        docToSave.uploaderName = fileData.name.split('_').shift();
        docToSave.categoryOfUpload = fileData.name.split('.').pop();
        docToSave.documentName = fileData.name;
        docToSave.editorFullName = this.authStore.user.fullName;
        docToSave.editorEmail = this.authStore.user.email;
        await saveEditDocument(docToSave);
      } else if (this.moduleType?.includes('External Appeal Supporting Documents')) {
        // NO-OP - Handled via form submission
      } else if (this.moduleType?.includes('Consumer Complaint Supporting Documents')) {
        // NO-OP - Handled via form submission
      } else {
        docToSave.version = 1;
        docToSave.moduleType = this.moduleType;
        docToSave.uploaderName = fileData.name.split('_').shift();
        docToSave.categoryOfUpload = fileData.name.split('.').pop();
        docToSave.documentApproved = false;
        docToSave.isDeleted = false;
        docToSave.documentName = fileData.name;
        docToSave.acceptedFileTypes = this.accept;
        docToSave.label = this.label;
        docToSave.refName = this.name;
        docToSave.entityUniqueId = this.entity;
        docToSave.entityName = this.appStore.getEntityNameById(this.entity);
        docToSave.networkUniqueId = this.network;
        docToSave.networkId = this.appStore.getNetworkIdById(this.network);
        docToSave.networkName = this.appStore.getNetworkNameById(this.network);
        docToSave.creatorFullName = this.authStore.user.fullName;
        docToSave.creatorEmail = this.authStore.user.email;
        await saveDocument(docToSave);
      }
    },

    clear() {
      this.$refs.fileUploadRef.uploadedFileCount = 0;
      this.$refs.fileUploadRef.files = [];
      this.$refs.fileUploadRef.messages = [];
      this.$refs.fileUploadRef.focused = false;
      this.$refs.fileUploadRef.progress = 0;
      this.$refs.fileUploadRef.uploadedFiles = [];
      // This clear function from the component doesn't do everything it should so also setting default state above.
      this.$refs.fileUploadRef.clear();
    },

    handleError(message) {
      this.errorMessage = message || 'Unexpected error occurred.';

      // Delay is important to let DOM render first
      this.$nextTick(() => {
        this.$refs.errorMessageRef?.$el?.focus();
      });
    },
  }
};
</script>

<style scoped>

</style>